import React from 'react';
import { clearAllStorage } from '../utils/storage';

const SegmentReport = ({ selectedSegments, confirmedMarket, redditAnalysis, onStartOver, onResetToStart }) => {
  const handleStartOver = () => {
    clearAllStorage();
    onStartOver();
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all data and return to the start?')) {
      clearAllStorage();
      onResetToStart();
    }
  };

  const groupSegmentsByApproach = (segments) => {
    if (!Array.isArray(segments)) {
      return {};
    }
    return segments.reduce((acc, segment) => {
      if (!acc[segment.approach]) {
        acc[segment.approach] = [];
      }
      acc[segment.approach].push(segment);
      return acc;
    }, {});
  };

  const groupedSegments = groupSegmentsByApproach(selectedSegments || []);

  const downloadReport = () => {
    const redditSection = redditAnalysis ? `
REAL USER INSIGHTS FROM REDDIT:
${redditAnalysis.summary || 'Analysis completed'}

KEY PAIN POINTS IDENTIFIED:
${redditAnalysis.keyPainPoints || 'No pain points identified'}

USER DEMOGRAPHICS:
${redditAnalysis.demographics || 'No demographic data available'}

MARKET OPPORTUNITIES:
${redditAnalysis.opportunities || 'No opportunities identified'}

NOTABLE USER QUOTES:
${redditAnalysis.notableQuotes || 'No quotes available'}

` : '';

    const reportContent = `
MARKET DISCOVERY REPORT
Generated: ${new Date().toLocaleDateString()}

CORE TARGET MARKET:
${confirmedMarket}

SELECTED MARKET SEGMENTS:
${Object.entries(groupedSegments).map(([approach, segments]) =>
  `\n${approach}:\n${segments.map(s => `  • ${s.category} → ${s.subcategory}`).join('\n')}`
).join('\n')}
${redditSection}

NEXT STEPS RECOMMENDATIONS:
1. Validate these segments through customer interviews
2. Test market demand with minimum viable products (MVPs)
3. Analyze competitive landscape for each segment
4. Develop segment-specific value propositions
5. Create targeted marketing strategies for priority segments

VALIDATION FRAMEWORK:
- Customer Discovery: Interview 10-15 potential customers per segment
- Problem Validation: Confirm the problem exists and is worth solving
- Solution Validation: Test if your solution addresses the problem
- Market Size: Estimate addressable market for each segment
- Business Model: Define how you'll monetize each segment

This report was generated using AI-powered market discovery and real Reddit conversations. Use it as a starting point for deeper market research and validation.
    `;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'market-discovery-report.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <button
        onClick={handleClearHistory}
        className="fixed top-4 right-4 px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors duration-200 z-10"
      >
        DEV: Clear History
      </button>
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Market Discovery Complete!</h1>
          <p className="text-lg text-gray-600">
            Your strategic market segmentation analysis is ready
          </p>
        </header>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Core Target Market</h2>
            <p className="text-lg text-blue-800 bg-blue-50 p-4 rounded-lg border border-blue-200">
              {confirmedMarket}
            </p>
          </div>

          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Selected Market Segments ({selectedSegments?.length || 0})
            </h3>
            
            {Object.entries(groupedSegments).map(([approach, segments]) => (
              <div key={approach} className="mb-6 last:mb-0">
                <h4 className="font-medium text-gray-800 mb-3 text-sm uppercase tracking-wide">
                  By {approach}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {segments.map((segment, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="font-medium text-gray-900 text-sm mb-1">
                        {segment.category}
                      </div>
                      <div className="text-gray-700 text-sm">
                        → {segment.subcategory}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reddit Insights Section */}
        {redditAnalysis && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl shadow-lg mb-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center">
                <span className="mr-2">🔍</span> Real User Insights from Reddit
              </h3>

              {redditAnalysis.summary && (
                <div className="mb-4">
                  <h4 className="font-medium text-orange-800 mb-2">Key Findings</h4>
                  <div className="text-orange-700 bg-white/50 p-3 rounded-lg border border-orange-200">
                    {redditAnalysis.summary}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {redditAnalysis.keyPainPoints && (
                  <div className="bg-white/30 p-3 rounded-lg border border-orange-200">
                    <h5 className="font-medium text-orange-800 mb-1">Pain Points</h5>
                    <div className="text-sm text-orange-700">{redditAnalysis.keyPainPoints}</div>
                  </div>
                )}

                {redditAnalysis.opportunities && (
                  <div className="bg-white/30 p-3 rounded-lg border border-orange-200">
                    <h5 className="font-medium text-orange-800 mb-1">Opportunities</h5>
                    <div className="text-sm text-orange-700">{redditAnalysis.opportunities}</div>
                  </div>
                )}
              </div>

              {redditAnalysis.notableQuotes && (
                <div className="mt-4 bg-white/40 p-3 rounded-lg border border-orange-200">
                  <h5 className="font-medium text-orange-800 mb-2">What Real Users Are Saying</h5>
                  <div className="text-sm text-orange-700 italic whitespace-pre-line">{redditAnalysis.notableQuotes}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps Recommendations</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                <div>
                  <h4 className="font-medium text-gray-900">Customer Validation</h4>
                  <p className="text-gray-600 text-sm">Interview 10-15 potential customers in each segment to validate problems and solutions</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                <div>
                  <h4 className="font-medium text-gray-900">MVP Testing</h4>
                  <p className="text-gray-600 text-sm">Create minimum viable products to test demand in priority segments</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                <div>
                  <h4 className="font-medium text-gray-900">Competitive Analysis</h4>
                  <p className="text-gray-600 text-sm">Research competitors and positioning opportunities in each segment</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                <div>
                  <h4 className="font-medium text-gray-900">Go-to-Market Strategy</h4>
                  <p className="text-gray-600 text-sm">Develop targeted marketing and sales strategies for each priority segment</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={downloadReport}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Report
            </div>
          </button>
          
          <button
            onClick={handleStartOver}
            className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-8 rounded-xl border-2 border-gray-300 hover:border-gray-400 transition-all duration-200"
          >
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Start New Discovery
            </div>
          </button>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            This analysis is a starting point for deeper market research. Validate with real customer feedback.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SegmentReport;